import {
  createStyles,
  Menu,
  Center,
  Header,
  Container,
  Group,
  Button,
  Modal,
  Burger,
  Title,
  Transition,
  TextInput,
  Textarea,
  LoadingOverlay,
  Box,
  Paper,
  rem,
} from '@mantine/core';
import {useForm} from '@mantine/form';
import {useAppStore} from '../../store';
import {toast} from 'react-hot-toast';
import {Link} from 'react-router-dom';
import {ContactInterface} from '../../models';
import {useDisclosure} from '@mantine/hooks';
import {IconChevronDown, IconArrowRight} from '@tabler/icons-react';
import logo from '../../assets/img/lo-go3.png';

const HEADER_HEIGHT = rem(100);

const useStyles = createStyles(theme => ({
  root: {
    position: 'relative',
    backgroundColor: '#F2F6F7',
    zIndex: 1,
  },
  inner: {
    height: HEADER_HEIGHT,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  links: {
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
  dropdown: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    left: 0,
    right: 0,
    zIndex: 0,

    padding: rem(5),
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderTopWidth: 0,
    overflow: 'hidden',

    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },

  burger: {
    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },

  link: {
    display: 'block',
    lineHeight: 1,
    padding: `${rem(8)} ${rem(12)}`,
    borderRadius: theme.radius.sm,
    textDecoration: 'none',
    color: '#21485e',
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,

    '&:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  linkLabel: {
    marginRight: rem(5),
  },
}));

interface HeaderActionProps {
  links: {
    link: string;
    label: string;
    links: {link: string; label: string}[];
  }[];
}

export function Navbar({links}: HeaderActionProps) {
  const {classes} = useStyles();
  const {contactStore} = useAppStore();

  const {loading, error} = contactStore.getters;
  const [opened, {toggle}] = useDisclosure(false);
  const [isOpened, {open, close}] = useDisclosure(false);
  const form = useForm({
    initialValues: {name: '', email: '', phone: '', message: ''},
    // functions will be used to validate values at corresponding key
    validate: {
      name: value =>
        value.length < 2 ? 'Name must have at least 2 letters' : null,
      email: value => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      phone: value =>
        value.length < 10 ? ' Phone must have at least 10 numbers' : null,
      message: value => (value.length < 10 ? 'Message is too short' : null),
    },
  });

  const sendCallbackMessage = async (formData: ContactInterface) => {
    await contactStore.actions.createContact(formData);
    if (error) {
      toast.error(error);
    } else {
      form.reset();
      toast.success('Message sent');
    }
  };

  const items = links.map(link => {
    const menuItems = link.links?.map(item => (
      <Menu.Item key={item.link} component="a" href={item.link} color="#21485e">
        {item.label}{' '}
      </Menu.Item>
    ));

    if (menuItems) {
      return (
        <Menu
          key={link.label}
          trigger="click"
          transitionProps={{exitDuration: 0}}
          withinPortal
        >
          <Menu.Target>
            <span className={classes.link}>
              <Center>
                <span className={classes.linkLabel}>{link.label}</span>
                <IconChevronDown size={rem(12)} stroke={1.5} />
              </Center>
            </span>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <a key={link.label} href={link.link} className={classes.link}>
        {link.label}
      </a>
    );
  });

  return (
    <Header
      height={HEADER_HEIGHT}
      sx={{borderBottom: 0}}
      mb={!opened ? 0 : 120}
      className={classes.root}
    >
      <Container className={classes.inner} fluid>
        <Burger
          opened={opened}
          onClick={toggle}
          className={classes.burger}
          size="sm"
        />
        <Link to="/">
          <img src={logo} alt="Logo" />
        </Link>
        <Group spacing={5} className={classes.links}>
          {items}
          <Menu
            key={'contact'}
            trigger="click"
            transitionProps={{exitDuration: 0}}
            withinPortal
          >
            <Menu.Target>
              <a className={classes.link} href="/contact">
                <Center>
                  <span className={classes.linkLabel}>Contact</span>
                </Center>
              </a>
            </Menu.Target>
          </Menu>
        </Group>

        <Transition transition="slide-left" duration={200} mounted={opened}>
          {styles => (
            <Paper className={classes.dropdown} withBorder style={styles}>
              {items}

              <Menu
                key={'contact'}
                trigger="click"
                transitionProps={{exitDuration: 0}}
                withinPortal
              >
                <Menu.Target>
                  <a className={classes.link} href="/contact">
                    <Center>
                      <span className={classes.linkLabel}>Contact</span>
                    </Center>
                  </a>
                </Menu.Target>
              </Menu>
            </Paper>
          )}
        </Transition>
        <Group>
          <Button
            onClick={open}
            leftIcon={<IconArrowRight size={rem(18)} />}
            styles={theme => ({
              root: {
                backgroundColor: '#485e21',
                border: 0,

                '&:not([data-disabled])': theme.fn.hover({
                  backgroundColor: '#21485e',
                  color: '#ffffff',
                }),
              },

              leftIcon: {
                marginRight: theme.spacing.md,
              },
            })}
          >
            Request callback
          </Button>
          {/* <Button
            component="a"
            href="/vacancies"
            rel="noopener noreferrer"
            leftIcon={<IconArrowRight size={rem(18)} />}
            styles={theme => ({
              root: {
                backgroundColor: '#485e21',
                border: 0,

                '&:not([data-disabled])': theme.fn.hover({
                  backgroundColor: '#21485e',
                  color: '#ffffff',
                }),
              },

              leftIcon: {
                marginRight: theme.spacing.md,
              },
            })}
          >
            Vacancies
          </Button> */}
        </Group>
      </Container>
      <>
        <Modal
          size="md"
          opened={isOpened}
          onClose={close}
          withCloseButton={true}
        >
          <Title mt={0} c="#485e21" order={2}>
            Request callback
          </Title>
          <Box maw={420} mx="auto">
            {loading ? (
              <LoadingOverlay
                loaderProps={{size: 'sm', color: 'green', variant: 'dots'}}
                overlayOpacity={0.3}
                overlayColor="#c5c5c5"
                visible
              />
            ) : (
              <>
                <form
                  className="mt-4"
                  onSubmit={form.onSubmit(values =>
                    sendCallbackMessage(values),
                  )}
                >
                  <TextInput
                    label="Name"
                    placeholder="Name"
                    withAsterisk
                    {...form.getInputProps('name')}
                  />
                  <TextInput
                    mt="sm"
                    label="Email"
                    withAsterisk
                    placeholder="Email"
                    {...form.getInputProps('email')}
                  />
                  <TextInput
                    mt="sm"
                    label="Telephone"
                    withAsterisk
                    placeholder="Telephone"
                    {...form.getInputProps('phone')}
                  />

                  <Textarea
                    label="Tell us about your enquiry"
                    placeholder="Your enquiry"
                    autosize
                    withAsterisk
                    minRows={6}
                    {...form.getInputProps('message')}
                  />
                  <Button
                    type="submit"
                    mt="sm"
                    styles={theme => ({
                      root: {
                        backgroundColor: '#21485e',
                        border: 0,
                        '&:not([data-disabled])': theme.fn.hover({
                          backgroundColor: '#21485e',
                          color: '#ffffff',
                        }),
                      },
                    })}
                  >
                    Submit
                  </Button>
                </form>
                <div className="mt-4">
                  <small>
                    This site is protected by reCAPTCHA and the Google Privacy
                    Policy and Terms of Service apply.
                  </small>
                </div>
              </>
            )}
          </Box>
        </Modal>
      </>
    </Header>
  );
}
